import { Alert, AlertDescription, AlertIcon, Stack } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { useNodeSecurity } from 'apps/TenantApp/hooks/useNodeSecurity';
import React from 'react';
import { graphql, useFragment } from 'utils/graphClient';
import { EventPermissionRules } from './EventPermissionRules';
import { FolderPermissionRules } from './FolderPermissionRules';
import { NodeSecurityRules } from './NodeSecurityRules';
import { PermissionRules_node$key } from './__generated__/PermissionRules_node.graphql';

const fragment = graphql`
  fragment PermissionRules_node on Node {
    ... on Event {
      isEvent: __typename
      ...EventPermissionRules_event
    }
    ... on Folder {
      isFolder: __typename
      ...FolderPermissionRules_folder
    }
    ...useNodeSecurity_node
  }
`;

interface PermissionRulesProps {
  node: PermissionRules_node$key;
}

export const PermissionRules = ({ node: _node }: PermissionRulesProps) => {
  const node = useFragment(fragment, _node);
  const { supportsNodeSecurity } = useNodeSecurity(node);

  if (!supportsNodeSecurity && !node.isEvent && !node.isFolder) {
    return <NoRulesPlaceHolder />;
  }

  return (
    <Stack spacing={4}>
      <NodeSecurityRules node={node} />
      <TypeSpecificPermissionRules node={_node} />
    </Stack>
  );
};

const TypeSpecificPermissionRules = ({ node: _node }: PermissionRulesProps) => {
  const node = useFragment(fragment, _node);

  if (node.isEvent) return <EventPermissionRules event={node} />;
  if (node.isFolder) return <FolderPermissionRules folder={node} />;
  return null;
};

const NoRulesPlaceHolder = () => (
  <Alert status="info" borderRadius="md">
    <AlertIcon />
    <AlertDescription>{t`This item does not have any configurable permissions.`}</AlertDescription>
  </Alert>
);
